import { cn } from '@/lib/utils';
import { MetricCard } from '@/components/shared/MetricCard';
import { ShoppingBag, CheckCircle, Clock, Star } from 'lucide-react';
import type { StoreStats, CategoryFilter } from '../types';
import { CATEGORY_CONFIG } from '../constants';

interface StoreStatsPanelProps {
    stats: StoreStats | undefined;
    categoryFilter: CategoryFilter;
    onCategoryChange: (cat: CategoryFilter) => void;
}

export function StoreStatsPanel({ stats, categoryFilter, onCategoryChange }: StoreStatsPanelProps) {
    return (
        <>
            {/* Metric Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <MetricCard
                    title="Total Materiales"
                    value={stats?.total_materials ?? 0}
                    icon={<ShoppingBag size={18} />}
                    iconColor="text-primary"
                />
                <MetricCard
                    title="Aprobados"
                    value={stats?.approved_count ?? 0}
                    icon={<CheckCircle size={18} />}
                    iconColor="text-emerald-400"
                />
                <MetricCard
                    title="Pendientes"
                    value={stats?.pending_count ?? 0}
                    icon={<Clock size={18} />}
                    iconColor="text-amber-400"
                />
                <MetricCard
                    title="Valoración Media"
                    value={stats?.avg_rating ? `${stats.avg_rating} ★` : '—'}
                    format="none"
                    icon={<Star size={18} />}
                    iconColor="text-yellow-400"
                />
            </div>

            {/* Category Breakdown */}
            <div className="rounded-xl border border-border bg-card p-5">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">
                    Distribución por Categoría
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                    {Object.entries(CATEGORY_CONFIG).map(([key, cfg]) => {
                        const count = stats ? (stats as any)[`category_${key}`] ?? 0 : 0;
                        return (
                            <button
                                key={key}
                                onClick={() => onCategoryChange(categoryFilter === key as CategoryFilter ? 'all' : key as CategoryFilter)}
                                className={cn(
                                    'flex items-center gap-2.5 px-3 py-2.5 rounded-lg border transition-all text-left',
                                    categoryFilter === key
                                        ? 'border-primary/50 bg-primary/10 ring-1 ring-primary/20'
                                        : 'border-border/50 bg-muted/20 hover:bg-muted/40 hover:border-border'
                                )}
                            >
                                <div className={cn('p-1.5 rounded-md bg-muted/50', cfg.color)}>
                                    {cfg.icon}
                                </div>
                                <div className="min-w-0">
                                    <p className="text-xs text-muted-foreground truncate">{cfg.label}</p>
                                    <p className="text-lg font-bold text-foreground leading-none mt-0.5">{count}</p>
                                </div>
                            </button>
                        );
                    })}
                </div>
            </div>
        </>
    );
}
